import { Controller } from "@hotwired/stimulus"

/**
 * Abstract controller to toggle behavior depending on the state of a checkbox, radio, or select input.
 * Subclasses override the `toggle(isToggled)` method to define behavior.
 * See show_by_controller.js for a usage example.
 *
 * @example Radio button
 *   <input type="radio" name="my-radio" value="a"> A
 *   <input type="radio" name="my-radio" value="b"> B
 *
 *   <div data-controller="show-by" data-show-by-radio="[name=my-radio]" data-show-by-value="a">
 *     Shown when A is selected
 *   </div>
 *
 * @example Checkbox
 *   <input type="checkbox" name="my-checkbox">
 *   <div data-controller="show-by" data-show-by-checkbox="[name=my-checkbox]">
 *     Shown when checked
 *   </div>
 *
 * @example Select
 *   <select id="my-select">
 *     <option value="x">X</option>
 *     <option value="y">Y</option>
 *   </select>
 *   <div data-controller="show-by" data-show-by-select="#my-select" data-show-by-value="x">
 *     Shown when X is selected
 *   </div>
 *
 * @example Multiple values
 *   <div data-controller="show-by" data-show-by-radio="[name=my-radio]" data-show-by-values="a,b">
 *     Shown when A or B is selected
 *   </div>
 */
export default class extends Controller {
  connect() {
    this.value = this.data.get("value")
    this.values = this.data.get("values")
      ? this.data.get("values").split(",")
      : []

    this.checkboxTargets = this._queryTargets("checkbox")
    this.radioTargets = this._queryTargets("radio")
    this.selectTargets = this._queryTargets("select")
    this.inputTargets = this._queryTargets("input")

    this._bindListeners(this.checkboxTargets, this._handleCheckboxChange)
    this._bindListeners(this.radioTargets, this._handleRadioChange)
    this._bindListeners(this.selectTargets, this._handleSelectChange)
    this._bindListeners(this.inputTargets, this._handleInputChange)

    // Set initial state
    if (this.checkboxTargets.length) this._handleCheckboxChange()
    if (this.radioTargets.length) this._handleRadioChange()
    if (this.selectTargets.length) this._handleSelectChange()
    if (this.inputTargets.length) this._handleInputChange()
  }

  disconnect() {
    this._unbindListeners(this.checkboxTargets, this._handleCheckboxChange)
    this._unbindListeners(this.radioTargets, this._handleRadioChange)
    this._unbindListeners(this.selectTargets, this._handleSelectChange)
    this._unbindListeners(this.inputTargets, this._handleInputChange)
  }

  // Subclasses override this method
  toggle(_isToggled) {}

  // Private

  _handleCheckboxChange = () => {
    const isToggled = Array.from(this.checkboxTargets).every((el) => el.checked)
    this.toggle(isToggled)
  }

  _handleRadioChange = () => {
    const checked = Array.from(this.radioTargets).find((el) => el.checked)
    this.toggle(checked ? this._matchesValue(checked.value) : false)
  }

  _handleSelectChange = () => {
    const select = this.selectTargets[0]
    this.toggle(select ? this._matchesValue(select.value) : false)
  }

  _handleInputChange = () => {
    const input = this.inputTargets[0]
    this.toggle(input ? this._matchesValue(input.value) : false)
  }

  _matchesValue(targetValue) {
    return this.value === targetValue || this.values.includes(targetValue)
  }

  _queryTargets(type) {
    const selector = this.data.get(type)
    if (!selector) return []
    return Array.from(document.querySelectorAll(selector))
  }

  _bindListeners(targets, handler) {
    targets.forEach((el) => el.addEventListener("change", handler))
  }

  _unbindListeners(targets, handler) {
    targets.forEach((el) => el.removeEventListener("change", handler))
  }
}
